import * as React from "react";
import * as Frontend from "kmmp/frontend";
import { useMemo } from "react";
import { PurchaseOrderIdBox } from "../purchase_order_id_box";
import { If, Repeater } from "shared/components";
import { MaybeError } from "shared/components/maybe-error";
import { QuantitySelector } from "../quantity_selector";
import { Option } from "@nozzlegear/railway";
import { SampleFrameNames } from "shared/sample-frames";
import { ExhaustiveCheckError } from "shared/errors";

type Props = {
    type: "packages" | "frames" | "corners";
    error: string | null

    onSetQuantity: (frame: Frontend.SampleFrameName, newValue: number) => void;
    quantities: Frontend.SampleQuantitiesRecord;

    purchaseOrderId: string;
    onSetPurchaseOrderId: (id: string) => void;
    showPurchaseOrderIdInput: boolean;

    instructions?: string | null;
    onSetInstructions?: (instructions: string) => void;
    showInstructionsInput: boolean;

    onPrimaryButtonClick: () => void;
    onSecondaryButtonClick?: () => void;
    primaryButtonText: string;
    secondaryButtonText?: string
}

export function Controls(props: Props): React.ReactElement {
    const quantityMap = useMemo(() => new Map(Object.entries(props.quantities)), [props.quantities]);

    const onClickButton = (e: React.MouseEvent<any>) => {
        e.preventDefault();
        props.onPrimaryButtonClick();
    };

    const onClickSecondaryButton = (e: React.MouseEvent<any>) => {
        e.preventDefault();
        props.onSecondaryButtonClick?.();
    };

    const onSetInstructions = React.useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
        props.onSetInstructions?.(e.currentTarget.value);
    }, [props.onSetInstructions]);

    let labelSuffix: string;
    switch (props.type) {
        case "packages":
            labelSuffix = " package samples";
            break;
        case "frames":
            labelSuffix = " sample frames";
            break;
        case "corners":
            labelSuffix = " corner samples";
            break;
        default:
            throw new ExhaustiveCheckError(props.type);
    }

    return (
        <div key={"all-controls"}>
            <div key={"generic-form-controls"}>
                <If condition={props.showPurchaseOrderIdInput}>
                    <PurchaseOrderIdBox
                        value={props.purchaseOrderId}
                        onChange={props.onSetPurchaseOrderId}
                    />
                </If>
                <Repeater source={SampleFrameNames}>
                    {(frame) =>
                        (<FrameQuantitySelector
                            key={frame}
                            frame={frame}
                            labelSuffix={labelSuffix}
                            quantity={quantityMap.get(frame) ?? 0}
                            onSetQuantity={props.onSetQuantity}
                         />)
                    }
                </Repeater>
                <If condition={props.showInstructionsInput}>
                    <div className="selector-container">
                        <p>
                            {`Special instructions`}
                            <small className="pull-right" style={{ float: "right" }}>
                                Optional
                            </small>
                        </p>
                        <textarea
                            rows={4}
                            className="form-control selector"
                            value={props.instructions ?? ""}
                            onChange={onSetInstructions}
                            placeholder="Enter notes or instructions for this order."
                        />
                    </div>
                </If>
                <div className="selector-container">
                    <MaybeError error={props.error} />
                    <div className="buttons">
                        <div className="secondary">
                            <If condition={Boolean(props.secondaryButtonText)}>
                                <button type="button" className="btn" onClick={onClickSecondaryButton}>
                                    {props.secondaryButtonText}
                                </button>
                            </If>
                        </div>
                        <div className="primary">
                            <button type="button" className="btn blue" onClick={onClickButton}>
                                {props.primaryButtonText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <hr className="marBottom25" />
        </div>
    );
}

type FrameQuantitySelectorProps = {
    labelSuffix: string;
    frame: Frontend.SampleFrameName;
    quantity: number;
    onSetQuantity: (frame: Frontend.SampleFrameName, value: number) => void;
}

function FrameQuantitySelector(props: FrameQuantitySelectorProps): React.ReactElement {
    const setQuantity = (value: number) => props.onSetQuantity(props.frame, value);
    const label = props.frame + " " + props.labelSuffix;

    return (
        <QuantitySelector
            min={0}
            max={10}
            quantity={props.quantity}
            onChange={setQuantity}
            leftLabel={label}
            rightLabel={Option.ofNone()}
        />
    )
}
